import type { StandardSubtitleProps } from './types'
import type { EventDescription } from '~/composables/types/event'
import { eventGetters } from '~/utils/eventGetters'
import { formatSmartPriceAmount } from '~/utils/currencyHelpers'

const getDefaultPriceFormatted = (
  kftEvent: EventDescription | undefined,
  localeBCP47: string
): string | undefined => {
  const amount = eventGetters.getDefaultPriceAmount(kftEvent)
  const currency = eventGetters.getDefaultPriceCurrency(kftEvent)
  if (!amount) return undefined
  return formatSmartPriceAmount(Number(amount), localeBCP47, currency)
}

export const getStandardPriceSubtitleText = (
  kftEvent: EventDescription | undefined,
  localeBCP47: string,
  t: (key: string, params?: Record<string, unknown>) => string
): string => {
  const price = getDefaultPriceFormatted(kftEvent, localeBCP47)
  if (!price) return ''

  const translationKey = eventGetters.isBoxEvent(kftEvent)
    ? 'product.show.checkout.priceSubtitleKit'
    : 'product.show.checkout.priceSubtitle'

  return t(translationKey, { price })
}

export const getStandardFormattedSubtitle = (
  kftEvent: EventDescription | undefined,
  localeBCP47: string,
  originalPricePerPerson: string | undefined,
  hasSale: boolean,
  hasSingleCategory: boolean,
  t: (key: string, params?: Record<string, unknown>) => string
): string => {
  const discountedPrice = getDefaultPriceFormatted(kftEvent, localeBCP47)
  if (!discountedPrice) return ''

  const shouldShowOriginalPrice =
    originalPricePerPerson && hasSale && hasSingleCategory

  const translationKey = eventGetters.isBoxEvent(kftEvent)
    ? 'product.show.checkout.priceSubtitleKit'
    : 'product.show.checkout.priceSubtitle'

  if (!shouldShowOriginalPrice) {
    return getStandardPriceSubtitleText(kftEvent, localeBCP47, t)
  }

  const tempPlaceholder = '__PRICE_PLACEHOLDER__'
  const translationWithPlaceholder = t(translationKey, {
    price: tempPlaceholder,
  })

  return translationWithPlaceholder.replace(
    tempPlaceholder,
    `<span class="line-through text-gray-400">${originalPricePerPerson}</span> ${discountedPrice}`
  )
}

export const getStandardSubtitleProps = (
  formattedSubtitle: string
): StandardSubtitleProps => ({
  variant: 'standard',
  formattedSubtitle,
})
